import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  IconButton,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  AccordionActions,
  CircularProgress,
  Container,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Typography, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import CreateIcon from "@mui/icons-material/Create";
// import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import ResponsiveDialogManager from "../dialog/ResponsiveDialogManager";
import { myExpariencesService } from "../../services";
import { useSnackbar } from "notistack";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ImageCropper from "../imageCropper/ImageCropper";
import YouTubeIcon from "@mui/icons-material/YouTube";

const PREFIX = "VrWatermark";

const classes = {
  iconColumn: `${PREFIX}-iconColumn`,
  linkColumn: `${PREFIX}-linkColumn`,
  deleteColumn: `${PREFIX}-deleteColumn`,
  helper: `${PREFIX}-helper`,
  ActionsIcons: `${PREFIX}-ActionsIcons`,
  marginActions: `${PREFIX}-marginActions`,
  details: `${PREFIX}-details`,
  watermarkLine: `${PREFIX}-watermarkLine`,
  watermarkLineContainer: `${PREFIX}-watermarkLineContainer`,
  watermarkSecondLinePadding: `${PREFIX}-watermarkSecondLinePadding`,
  extraLeftPadding: `${PREFIX}-extraLeftPadding`,
  borderBottomLine: `${PREFIX}-borderBottomLine`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  editImageIconMyDesign: `${PREFIX}-editImageIconMyDesign`,
  iconContainer: `${PREFIX}-iconContainer`,
  iconImage: `${PREFIX}-iconImage`,
  displayNone: `${PREFIX}-displayNone`,
  saveExternalLinksButton: `${PREFIX}-saveExternalLinksButton`,
  addCustomWatermarkButton: `${PREFIX}-addCustomWatermarkButton`,
  addVideoWatermarkButton: `${PREFIX}-addVideoWatermarkButton`,
  marginTop: `${PREFIX}-marginTop`,
  hiddingVrForLoading: `${PREFIX}-hiddingVrForLoading`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.iconColumn}`]: {
    [theme.breakpoints.between("xs", "md")]: {
      flexBasis: "33.3%",
    },
    flexBasis: "18.3%",
    position: "relative",
  },

  [`& .${classes.linkColumn}`]: {
    flexBasis: "80%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  [`& .${classes.deleteColumn}`]: {
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.helper}`]: {
    padding: "8px",
  },

  [`& .${classes.ActionsIcons}`]: {
    cursor: "pointer",
  },

  [`& .${classes.marginActions}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.details}`]: {
    width: "100%",
    maxHeight: "300px",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },

  [`& .${classes.watermarkLine}`]: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.watermarkLineContainer}`]: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    maxHeight: "65px",
  },

  [`& .${classes.watermarkSecondLinePadding}`]: {
    padding: "0 2.5rem 0 0",
  },

  [`& .${classes.extraLeftPadding}`]: {
    paddingLeft: "0.5rem",
  },

  [`& .${classes.borderBottomLine}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.editImageIcon}`]: {
    display: "flex",
    zIndex: "90",
    position: "absolute",
    top: "-20px",
    [theme.breakpoints.up("sm")]: {
      left: "60px",
    },
    [theme.breakpoints.between("xs", "md")]: {
      right: "-15px",
    },
  },

  [`& .${classes.editImageIconMyDesign}`]: {
    display: "flex",
    zIndex: "90",
    position: "absolute",
    top: "-20px",
    left: "60px",
    [theme.breakpoints.between("xs", "md")]: {
      left: "25px",
    },
  },

  [`& .${classes.iconContainer}`]: {
    transform: "translateY(12px)",
  },

  [`& .${classes.addCustomWatermarkButton}`]: {
    transform: "translateY(-32px)",
    width: "100%",
    marginBottom: "5px",
  },

  [`& .${classes.addVideoWatermarkButton}`]: {
    transform: "translateY(-50px)",
  },

  [`& .${classes.iconImage}`]: {
    borderRadius: "10px",
    maxWidth: "60px",
    width: "100%",
  },

  [`& .${classes.displayNone}`]: {
    display: "none",
  },

  [`& .${classes.saveExternalLinksButton}`]: {
    "&:disabled": {
      backgroundColor: "transparent",
    },
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.hiddingVrForLoading}`]: {
    padding: "1rem",
    textAlign: "center",
  },
}));

const VrWatermark = (props) => {
  const { t } = useTranslation();
  const [vrWatermarkElements, setVrWatermarkElements] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [open, setOpen] = useState(false);
  const [newIcon, setNewIcon] = useState(null);
  const [lastIndex, setLastIndex] = useState(null);
  const [lockSaving, setLockSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  function toggleDialog() {
    setOpen((prevState) => !prevState);
  }

  useEffect(() => {
    if (props.externalLinks) {
      setVrWatermarkElements(props.externalLinks);
      setInitialized(true);
    }
  }, [props.externalLinks]);

  useEffect(() => {
    if (!props.isSaveEnabled) {
      if (
        vrWatermarkElements?.length === props.externalLinks?.length &&
        !isInitialized
      )
        setIsInitialized(true);
      if (isInitialized) {
        props.saveModifications(vrWatermarkElements, "externalLinks");
      }
    }
  }, [vrWatermarkElements]);

  const handleModifyElement = (index, id, value) => {
    const tmp = [...vrWatermarkElements].map((item) => ({ ...item }));
    tmp[index][id] = value;
    setVrWatermarkElements(tmp);
  };

  const handleDeleteLine = (index) => {
    const tmp = [...vrWatermarkElements];
    tmp.splice(index, 1);
    setVrWatermarkElements(tmp);
  };

  const handleAddLine = () => {
    const tmp = [...vrWatermarkElements];
    tmp.push({
      logo: null,
      link: "",
      fileName: null,
      rapidAccess: false,
      title: "",
    });
    setVrWatermarkElements(tmp);
  };

  const handleSaveCustomWatermarks = () => {
    setLockSaving(true);
    const tmp = [...vrWatermarkElements];
    let files = [];
    let formatedObject = [];
    tmp.forEach((elem) => {
      if (elem.fileName)
        files.push({ file: elem.logo, fileName: elem.fileName });
      formatedObject.push({
        logo: elem.fileName ? elem.fileName : elem.logo,
        link: elem.link,
        rapidAccess: elem.rapidAccess,
        title: elem.title,
      });
    });
    if (props.exparience) {
      myExpariencesService
        .saveExternalLinks(props.exparience.id, formatedObject, files)
        .then(
          (res) => {
            props.setExparience({
              ...props.exparience,
              externalLinks: res.externalLinks,
            });
            setLockSaving(false);
            enqueueSnackbar(t("toasts.updateExternalLinksSuccess"), {
              variant: "success",
            });
          },
          (error) => {
            setLockSaving(false);
            enqueueSnackbar(t("toasts.updateExternalLinksError"), {
              variant: "error",
            });
          }
        );
    }
  };

  const isSaveEnabled = () => {
    const tmp = [...vrWatermarkElements];
    let disabled = false;
    tmp.forEach((element) => {
      if (!element.logo || !element.link || !element.title) {
        disabled = true;
      }
    });
    return disabled;
  };

  // function saveIcon(isVideoWatermark) {
  //   const tmp = [...vrWatermarkElements];
  //   tmp[lastIndex] = {
  //     ...tmp[lastIndex],
  //     logo: isVideoWatermark
  //       ? "https://exparience.s3.fr-par.scw.cloud/watermark-buttons-image/playButton.png"
  //       : document.getElementById("cropping-canvas").toDataURL(),
  //   };
  //   setVrWatermarkElements(tmp);
  //   !isVideoWatermark && toggleDialog();
  // }

  function saveIcon() {
    const tmp = [...vrWatermarkElements];
    tmp[lastIndex] = {
      ...tmp[lastIndex],
      logo: document.getElementById("cropping-canvas").toDataURL(),
    };
    setVrWatermarkElements(tmp);
    toggleDialog();
  }

  function saveWatermarkIcon(index) {
    const tmp = [...vrWatermarkElements];
    tmp[index] = {
      ...tmp[index],
      logo: "https://exparience.s3.fr-par.scw.cloud/watermark-buttons-image/playButton.png",
    };
    setVrWatermarkElements(tmp);
  }

  function saveFileName(index, fileName) {
    const tmp = [...vrWatermarkElements];
    const newFileName = fileName.replace(/\s+/g, "");
    tmp[index] = { ...tmp[index], fileName: Date.now() + "-" + newFileName };
    setVrWatermarkElements(tmp);
  }

  const handleWatermarkOrder = (order, index) => {
    const tmpElements = [...vrWatermarkElements];
    if (
      (order === "up" && index === 0) ||
      (order === "down" && index === vrWatermarkElements.length - 1)
    )
      return;
    if (order === "up") {
      const tmp = tmpElements[index];
      tmpElements[index] = tmpElements[index - 1];
      tmpElements[index - 1] = tmp;
    } else {
      const tmp = tmpElements[index];
      tmpElements[index] = tmpElements[index + 1];
      tmpElements[index + 1] = tmp;
    }
    setVrWatermarkElements(tmpElements);
  };

  if (!initialized) {
    return (
      <Root>
        <div className={classes.hiddingVrForLoading}>
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </Root>
    );
  } else {
    return (
      <Root>
        <ResponsiveDialogManager
          title={t("myExpariences.watermarkExternalCropModalTitle")}
          // mobile={location.pathname.indexOf("mobile") > 0}
          open={open}
          onClose={toggleDialog}
          renderer={
            <ImageCropper
              imgUrl={newIcon}
              aspect={1 / 1}
              urlSetter={setNewIcon}
            />
          }
          toolbarActions={
            <Container>
              <Button
                sx={{ backgroundColor: "#424242", marginRight: "1rem" }}
                onClick={saveIcon}
              >
                {t("account.validate")}
              </Button>
              <Button
                sx={{ backgroundColor: "#424242" }}
                onClick={toggleDialog}
              >
                {t("account.cancel")}
              </Button>
            </Container>
          }
        />
        <Accordion
          open={props.displayCustomIcon}
          defaultExpanded
          style={{
            margin: !props.isSaveEnabled ? "0" : "16px 0",
            backgroundColor: "#424242",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls="watermark-VR-content"
            id="watermark-VR-header"
          >
            <div className={classes.iconColumn}>
              <Typography className={classes.heading}>
                {t("myExpariences.watermarkExternalLinks")}
              </Typography>
            </div>
            <Typography variant="body2" className={classes.linkColumn}>
              {`${vrWatermarkElements.length} ${t(
                "myExpariences.watermarkExternalElements"
              )}`}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            {vrWatermarkElements.map((element, index) => (
              <div
                key={`watermark-custom-${index + 1}`}
                className={clsx(
                  classes.watermarkLine,
                  index < vrWatermarkElements.length - 1
                    ? classes.borderBottomLine
                    : ""
                )}
              >
                <div className={classes.watermarkLineContainer}>
                  <div className={classes.iconColumn}>
                    <div className={classes.iconContainer}>
                      {element.logo && (
                        <div
                          className={
                            props.isSaveEnabled
                              ? classes.editImageIcon
                              : classes.editImageIconMyDesign
                          }
                        >
                          <input
                            type="file"
                            accept="image/*"
                            name="modify-custom-watermark-icon"
                            id="modify-custom-watermark-icon"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const reader = new FileReader();
                              reader.onloadend = (value) => {
                                setNewIcon(value.target.result);
                              };
                              toggleDialog();
                              saveFileName(index, e.target.files[0].name);
                              reader.readAsDataURL(e.target.files[0]);
                              e.target.value = "";
                            }}
                          />
                          <label htmlFor="modify-custom-watermark-icon">
                            <IconButton
                              color="primary"
                              aria-label="upload watermark icon"
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLastIndex(index);
                              }}
                              size="large"
                            >
                              <CreateIcon />
                            </IconButton>
                          </label>
                        </div>
                      )}
                      <img
                        id="customWatermarkIcon"
                        className={classes.iconImage}
                        style={{
                          border: element.logo
                            ? "1px solid rgba(211, 175, 95, 0.3)"
                            : "none",
                        }}
                        alt={element.logo ? "custom watermark logo" : null}
                        src={element.logo ? element.logo : undefined}
                      />
                      {!element.logo && (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            name="customWatermarkIcon"
                            id="icon-button-add-custom-watermark"
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const reader = new FileReader();
                              reader.onloadend = (value) => {
                                setNewIcon(value.target.result);
                              };
                              toggleDialog();
                              saveFileName(index, e.target.files[0].name);
                              reader.readAsDataURL(e.target.files[0]);
                              e.target.value = "";
                            }}
                          />
                          <label htmlFor="icon-button-add-custom-watermark">
                            <Button
                              variant="contained"
                              color="primary"
                              component="span"
                              size="small"
                              className={classes.addCustomWatermarkButton}
                              onClick={() => setLastIndex(index)}
                            >
                              {t("myExpariences.watermarkExternalAddButton")}
                            </Button>
                          </label>

                          <IconButton
                            variant="contained"
                            color="primary"
                            component="span"
                            // size="small"
                            className={classes.addVideoWatermarkButton}
                            onClick={() => {
                              saveWatermarkIcon(index);
                            }}
                          >
                            <YouTubeIcon sx={{ fontSize: "55px" }} />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={clsx(classes.linkColumn, classes.helper)}>
                    <TextField
                      name={`link-custom-watermark-${index + 1}`}
                      label={t("myExpariences.form.watermarkRedirectLink")}
                      value={element.link}
                      onChange={(e) =>
                        handleModifyElement(index, "link", e.target.value)
                      }
                      fullWidth
                      variant={"outlined"}
                    />
                  </div>
                  <div className={clsx(classes.deleteColumn)}>
                    <IconButton
                      size="small"
                      onClick={() => handleWatermarkOrder("up", index)}
                      className={classes.marginActions}
                    >
                      <KeyboardArrowUpIcon
                        color="primary"
                        className={classes.ActionsIcons}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteLine(index)}
                      className={classes.marginActions}
                    >
                      <ClearIcon
                        color="primary"
                        className={classes.ActionsIcons}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleWatermarkOrder("down", index)}
                      className={classes.marginActions}
                    >
                      <KeyboardArrowDownIcon
                        color="primary"
                        className={classes.ActionsIcons}
                      />
                    </IconButton>
                  </div>
                </div>
                <div
                  className={clsx(
                    classes.watermarkLineContainer,
                    classes.marginTop,
                    classes.watermarkSecondLinePadding
                  )}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color={"primary"}
                        onChange={(e) =>
                          handleModifyElement(
                            index,
                            "rapidAccess",
                            e.target.checked
                          )
                        }
                        checked={element.rapidAccess}
                      />
                    }
                    label={
                      <Typography variant="body1">
                        {t("myExpariences.form.rapidAccessCheckbox")}
                      </Typography>
                    }
                    className={clsx(
                      classes.marginBottom,
                      props.isSaveEnabled ? classes.extraLeftPadding : ""
                    )}
                    name={`rapid-access-custom-watermark-${index + 1}`}
                  />
                  <TextField
                    name={`title-custom-watermark-${index + 1}`}
                    label={t("myExpariences.form.customWatermarkLinkTitle")}
                    value={element.title}
                    onChange={(e) =>
                      handleModifyElement(index, "title", e.target.value)
                    }
                    fullWidth
                    variant={"outlined"}
                  />
                </div>
                <Divider
                  light
                  className={clsx(classes.marginBottom, classes.marginTop)}
                />
              </div>
            ))}
          </AccordionDetails>
          <Divider />
          <AccordionActions
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Button
              size="small"
              color="primary"
              startIcon={!lockSaving ? <AddIcon /> : null}
              onClick={() => handleAddLine()}
            >
              {!lockSaving && t("myExpariences.watermarkExternalAddButton")}
            </Button>
            {props.isSaveEnabled && (
              <Button
                size="small"
                color="primary"
                onClick={() => handleSaveCustomWatermarks()}
                disabled={isSaveEnabled() || lockSaving}
                className={classes.saveExternalLinksButton}
              >
                {lockSaving ? (
                  <CircularProgress size={28} />
                ) : (
                  t("myExpariences.watermarkExternalSaveButton")
                )}
              </Button>
            )}
          </AccordionActions>
        </Accordion>
      </Root>
    );
  }
};

export default VrWatermark;
